import type { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { SeverityLevel, trackEvent, trackException } from './appInsights';

/**
 * Classification of a failed receipt-email submission. Chosen so the
 * production support team can map a failure to one of the diagnostic
 * categories in the Mastercard receipt-delivery acceptance criteria.
 */
export type ReceiptFailureCategory =
	/** Browser refused the request or the request never reached origin.
	 *  Indistinguishable from the browser side: CSP block, CORS preflight
	 *  failure, DNS failure, offline, TLS failure, WAF reset. Correlate with
	 *  edge logs by `correlationId` to narrow down. */
	| 'network_or_blocked'
	/** axios `ECONNABORTED` — the 10s client-side timeout fired. */
	| 'timeout'
	/** Request aborted (navigation / unmount / explicit AbortController). */
	| 'cancelled'
	/** Response was received but status was non-2xx. */
	| 'non_2xx'
	| 'unknown';

export interface ReceiptSubmitResult {
	ok: boolean;
	correlationId: string;
	status?: number;
	durationMs: number;
	category?: ReceiptFailureCategory;
	errorMessage?: string;
}

export interface InstrumentReceiptOptions {
	api: AxiosInstance;
	url: string;
	payload: unknown;
	/** Request header carrying the correlation id. Defaults to `x-correlation-id`. */
	correlationHeader?: string;
	/** Overrides the URL used for the sendBeacon fallback on page unload. */
	beaconUrl?: string;
}

const EVENT = 'ReceiptEmailSubmit';

const generateCorrelationId = (): string => {
	const c = typeof crypto !== 'undefined' ? crypto : undefined;
	if (c && typeof c.randomUUID === 'function') return c.randomUUID();
	// RFC4122 v4 built from CSPRNG bytes when randomUUID() is unavailable (older browsers).
	if (c && typeof c.getRandomValues === 'function') {
		const bytes = new Uint8Array(16);
		c.getRandomValues(bytes);
		bytes[6] = (bytes[6] & 0x0f) | 0x40;
		bytes[8] = (bytes[8] & 0x3f) | 0x80;
		const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
		return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
	}
	// Non-cryptographic last-resort id for log correlation only; not used for auth, tokens, or secrets. NOSONAR
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
		const r = (Math.random() * 16) | 0; // NOSONAR: non-security log correlation only
		const v = ch === 'x' ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
};

const classify = (error: AxiosError): ReceiptFailureCategory => {
	if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError' || error.name === 'AbortError') {
		return 'cancelled';
	}
	if (error.code === 'ECONNABORTED') return 'timeout';
	if (error.response) return 'non_2xx';
	if (error.code === 'ERR_NETWORK') return 'network_or_blocked';
	return 'unknown';
};

/** The response interceptor in `getApiInstance` wraps rejections as `{error}`. */
const unwrapAxiosError = (raw: unknown): AxiosError => {
	if (raw && typeof raw === 'object' && 'error' in (raw as object)) {
		return (raw as { error: AxiosError }).error;
	}
	return raw as AxiosError;
};

const now = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());

const resolveAbsoluteUrl = (url: string, baseURL?: string): string => {
	const locationHref = typeof location !== 'undefined' ? location.href : 'http://localhost/';
	try {
		const base = baseURL ? new URL(baseURL, locationHref).toString() : locationHref;
		return new URL(url, base).toString();
	} catch {
		return url;
	}
};

const safePayloadSize = (payload: unknown): number => {
	try {
		return new Blob([JSON.stringify(payload)]).size;
	} catch {
		return 0;
	}
};

/**
 * Arms a sendBeacon as a last-ditch retry if the page is torn down before
 * the primary axios call resolves. The beacon is best-effort: Content-Type
 * and custom headers cannot be set, so the backend must be prepared to
 * accept an unauthenticated JSON blob to use this as a real fallback.
 *
 * Only listens for `pagehide` (actual page teardown). `visibilitychange`
 * fires on tab switch / minimize while the primary request is still in
 * flight, which would duplicate the submission.
 */
const armBeaconFallback = (
	absoluteUrl: string,
	payload: unknown,
	correlationId: string,
	isPrimarySettled: () => boolean,
): (() => void) => {
	if (
		typeof navigator === 'undefined' ||
		typeof navigator.sendBeacon !== 'function' ||
		typeof window === 'undefined'
	) {
		return () => undefined;
	}
	let fired = false;
	const fire = (): void => {
		if (fired || isPrimarySettled()) return;
		fired = true;
		try {
			const body = { ...(payload as Record<string, unknown>), _correlationId: correlationId };
			const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
			navigator.sendBeacon(absoluteUrl, blob);
		} catch {
			// best-effort — swallow
		}
	};
	window.addEventListener('pagehide', fire);
	return () => {
		// Mark fired so a late pagehide (after primary settles) is a no-op.
		fired = true;
		window.removeEventListener('pagehide', fire);
	};
};

/**
 * Instrumented wrapper around the receipt-email POST. Emits structured
 * console logs plus Application Insights events so a given submission can
 * be traced from the browser all the way to the Optimizely endpoint via
 * the `x-correlation-id` request header.
 */
export const instrumentReceiptSubmit = async ({
	api,
	url,
	payload,
	correlationHeader = 'x-correlation-id',
	beaconUrl,
}: InstrumentReceiptOptions): Promise<ReceiptSubmitResult> => {
	const correlationId = generateCorrelationId();
	const payloadSize = safePayloadSize(payload);
	const resolvedUrl = resolveAbsoluteUrl(url, api.defaults.baseURL);

	const attemptProps = { correlationId, url: resolvedUrl, method: 'POST', payloadSize };
	// eslint-disable-next-line no-console
	console.info('[ReceiptEmail] submit:attempt', attemptProps);
	trackEvent({ name: `${EVENT}.Attempt` }, attemptProps);

	let primarySettled = false;
	const cleanupBeacon = armBeaconFallback(beaconUrl ?? resolvedUrl, payload, correlationId, () => primarySettled);
	const start = now();

	try {
		const config: AxiosRequestConfig = {
			headers: { [correlationHeader]: correlationId },
		};
		const res = await api.post(url, payload, config);
		const durationMs = Math.round(now() - start);
		primarySettled = true;
		cleanupBeacon();

		const ok = typeof res?.status === 'number' && res.status >= 200 && res.status < 300;
		const result: ReceiptSubmitResult = {
			ok,
			correlationId,
			status: res?.status,
			durationMs,
			category: ok ? undefined : 'non_2xx',
		};
		// eslint-disable-next-line no-console
		console.info('[ReceiptEmail] submit:complete', result);
		trackEvent(
			{ name: ok ? `${EVENT}.Success` : `${EVENT}.Failure` },
			{
				correlationId: result.correlationId,
				status: result.status,
				durationMs: result.durationMs,
				category: result.category,
			},
		);
		return result;
	} catch (error_) {
		const durationMs = Math.round(now() - start);
		primarySettled = true;
		cleanupBeacon();

		const error = unwrapAxiosError(error_);
		const category = classify(error);
		const result: ReceiptSubmitResult = {
			ok: false,
			correlationId,
			status: error.response?.status,
			durationMs,
			category,
			errorMessage: error.message,
		};
		// eslint-disable-next-line no-console
		console.error('[ReceiptEmail] submit:error', result, error);
		trackException(
			{
				exception: error instanceof Error ? error : new Error(String(error)),
				severityLevel: category === 'non_2xx' ? SeverityLevel.Warning : SeverityLevel.Error,
			},
			{
				correlationId,
				url: resolvedUrl,
				status: result.status,
				durationMs,
				category,
				event: `${EVENT}.Exception`,
			},
		);
		trackEvent(
			{ name: `${EVENT}.Failure` },
			{
				correlationId,
				status: result.status,
				durationMs,
				category,
				errorMessage: result.errorMessage,
			},
		);
		return result;
	}
};
